import { useState, useEffect } from 'react';
import { 
  MessageSquare, Plus, Play, Pause, XCircle, RefreshCw, Settings, 
  Users, CheckCircle, AlertCircle, Clock, MoreVertical, Trash2,
  Phone, Send, Eye, Zap
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const RetargetingPage = () => {
  const [activeTab, setActiveTab] = useState('candidates');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eligibleCount, setEligibleCount] = useState(0);
  const [eligibleCandidates, setEligibleCandidates] = useState([]);
  const [showEligibleList, setShowEligibleList] = useState(false);
  
  // Modals
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  
  // Campaign form
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    template_name: 'cv_upload_candidate_ack1',
    link_base_url: 'https://daruwallas.com',
    min_delay: 2,
    max_delay: 20,
    start_time: '09:00',
    end_time: '18:00',
    active_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    daily_limit: 100,
    skip_time_window: false
  });
  
  // Progress
  const [progressCampaign, setProgressCampaign] = useState(null);
  const [progress, setProgress] = useState(null);
  
  // Phone cleanup
  const [showPhoneCleanup, setShowPhoneCleanup] = useState(false);
  const [phoneCleanupPreview, setPhoneCleanupPreview] = useState(null);
  const [phoneCleanupLoading, setPhoneCleanupLoading] = useState(false);
  const [phoneCleanupDone, setPhoneCleanupDone] = useState(false);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setCampaigns(data.campaigns || []);
    } catch (err) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchEligibleCount = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/draft/eligible-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEligibleCount(data.eligible_count || 0);
    } catch (err) {
      console.error('Failed to fetch eligible count');
    }
  };

  const fetchEligibleCandidates = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/eligible-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setEligibleCandidates(data.candidates || []);
      setShowEligibleList(true);
    } catch (err) {
      toast.error('Failed to fetch candidates list');
    }
  };

  const handlePhoneCleanupPreview = async () => {
    setPhoneCleanupLoading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/cleanup-phones?dry_run=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setPhoneCleanupPreview(data);
      setShowPhoneCleanup(true);
    } catch (err) {
      toast.error('Failed to preview phone cleanup');
    } finally {
      setPhoneCleanupLoading(false);
    }
  };

  const handlePhoneCleanupApply = async () => {
    setPhoneCleanupLoading(true);
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/cleanup-phones?dry_run=false`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      toast.success(`Cleaned ${data.updated} phone numbers!`);
      setPhoneCleanupDone(true);
      setShowPhoneCleanup(false);
      // Refresh eligible list
      fetchEligibleCount();
    } catch (err) {
      toast.error('Failed to apply phone cleanup');
    } finally {
      setPhoneCleanupLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchEligibleCount();
  }, []);

  const handleCreateCampaign = async () => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(campaignForm)
      });
      
      if (!res.ok) throw new Error('Failed to create campaign');
      
      toast.success('Campaign created');
      setShowCreateCampaign(false);
      resetForm();
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateCampaign = async () => {
    if (!editingCampaign) return;
    
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/${editingCampaign.id}`, {
        method: 'PUT',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(campaignForm)
      });
      
      if (!res.ok) throw new Error('Failed to update campaign');
      
      toast.success('Campaign updated');
      setEditingCampaign(null);
      setShowCreateCampaign(false);
      resetForm();
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Delete this campaign?')) return;
    
    try {
      const token = localStorage.getItem('ats_token');
      await fetch(`${API}/api/retargeting/candidate/campaigns/${campaignId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleStartCampaign = async (campaignId) => {
    if (!window.confirm('Start sending WhatsApp messages to eligible candidates?')) return;
    
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/${campaignId}/start`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to start');
      
      toast.success(data.message);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePauseCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/${campaignId}/pause`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to pause');
      toast.success('Campaign paused');
      fetchCampaigns();
      if (showProgress) fetchProgress(campaignId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/${campaignId}/resume`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to resume');
      toast.success('Campaign resumed');
      fetchCampaigns();
      if (showProgress) fetchProgress(campaignId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleStopCampaign = async (campaignId) => {
    if (!window.confirm('Stop this campaign? All pending messages will be cancelled.')) return;
    
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/${campaignId}/stop`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to stop');
      toast.success('Campaign stopped');
      fetchCampaigns();
      setShowProgress(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRestartCampaign = async (campaignId) => {
    if (!window.confirm('Restart this campaign? This will resend to ALL eligible candidates.')) return;
    
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/${campaignId}/restart`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to restart');
      
      toast.success(data.message);
      fetchCampaigns();
      if (showProgress) fetchProgress(campaignId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openProgressModal = async (campaign) => {
    setProgressCampaign(campaign);
    setShowProgress(true);
    await fetchProgress(campaign.id);
  };

  const fetchProgress = async (campaignId) => {
    try {
      const token = localStorage.getItem('ats_token');
      const res = await fetch(`${API}/api/retargeting/candidate/campaigns/${campaignId}/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setProgress(data);
      if (data.campaign) {
        setProgressCampaign(data.campaign);
      }
    } catch (err) {
      toast.error('Failed to fetch progress');
    }
  };

  const openEditModal = (campaign) => {
    setEditingCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      template_name: campaign.template_name || 'cv_upload_candidate_ack1',
      link_base_url: campaign.link_base_url || 'https://daruwallas.com',
      min_delay: campaign.min_delay || 2,
      max_delay: campaign.max_delay || 20,
      start_time: campaign.start_time || '09:00',
      end_time: campaign.end_time || '18:00',
      active_days: campaign.active_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      daily_limit: campaign.daily_limit || 100,
      skip_time_window: campaign.skip_time_window || false
    });
    setShowCreateCampaign(true);
  };

  const resetForm = () => {
    setCampaignForm({
      name: '',
      template_name: 'cv_upload_candidate_ack1',
      link_base_url: 'https://daruwallas.com',
      min_delay: 2,
      max_delay: 20,
      start_time: '09:00',
      end_time: '18:00',
      active_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
      daily_limit: 100,
      skip_time_window: false
    });
    setEditingCampaign(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-100 text-slate-700',
      sending: 'bg-yellow-100 text-yellow-700',
      paused: 'bg-orange-100 text-orange-700',
      completed: 'bg-green-100 text-green-700',
      stopped: 'bg-red-100 text-red-700',
      failed: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const toggleDay = (day) => {
    const days = campaignForm.active_days;
    if (days.includes(day)) {
      setCampaignForm({...campaignForm, active_days: days.filter(d => d !== day)});
    } else {
      setCampaignForm({...campaignForm, active_days: [...days, day]});
    }
  };

  const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  // Auto-refresh progress
  useEffect(() => {
    let interval;
    if (showProgress && progressCampaign && progressCampaign.status === 'sending') {
      interval = setInterval(() => fetchProgress(progressCampaign.id), 3000);
    }
    return () => clearInterval(interval);
  }, [showProgress, progressCampaign]);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Retargeting</h1>
          <p className="text-slate-500">WhatsApp campaigns to re-engage candidates and contacts</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="candidates" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Candidate Retargeting
          </TabsTrigger>
          <TabsTrigger value="hr" className="flex items-center gap-2" disabled>
            <MessageSquare className="w-4 h-4" />
            HR Contacts
            <Badge variant="outline" className="ml-1 text-xs">Coming Soon</Badge>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="candidates">
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={fetchEligibleCandidates}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{eligibleCount.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Eligible Candidates</p>
                    <p className="text-xs text-blue-500 mt-1">Click to view list</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {campaigns.reduce((sum, c) => sum + (c.stats?.sent || 0), 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">Messages Sent</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Zap className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {campaigns.filter(c => c.status === 'sending').length}
                    </p>
                    <p className="text-xs text-slate-500">Active Campaigns</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{campaigns.length}</p>
                    <p className="text-xs text-slate-500">Total Campaigns</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Info Card */}
          <Card className="mb-6 bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-teal-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-teal-800">Candidate WhatsApp Retargeting</h3>
                  <p className="text-sm text-teal-700 mt-1">
                    Send WhatsApp messages to candidates whose resumes were uploaded via ATS but haven't registered on the portal yet.
                    Messages are sent with smart random delays to avoid detection.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Phone Cleanup Banner - shows only if not done */}
          {!phoneCleanupDone && (
            <Card className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-amber-800">Phone Number Cleanup</h3>
                      <p className="text-sm text-amber-700 mt-1">
                        Remove country codes (+91, 0091) from phone numbers for proper WhatsApp delivery.
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handlePhoneCleanupPreview}
                    disabled={phoneCleanupLoading}
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                  >
                    {phoneCleanupLoading ? 'Loading...' : 'Clean Phone Numbers'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-end mb-4">
            <Button onClick={() => { resetForm(); setShowCreateCampaign(true); }}>
              <Plus className="w-4 h-4 mr-2" /> Create Campaign
            </Button>
          </div>

          {/* Campaigns List */}
          <Card>
            <CardContent className="p-0">
              <table className="w-full">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-3 text-left text-sm font-medium text-slate-600">Campaign</th>
                    <th className="p-3 text-left text-sm font-medium text-slate-600">Template</th>
                    <th className="p-3 text-left text-sm font-medium text-slate-600">Status</th>
                    <th className="p-3 text-left text-sm font-medium text-slate-600">Progress</th>
                    <th className="p-3 text-left text-sm font-medium text-slate-600">Delay</th>
                    <th className="p-3 text-right text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="p-8 text-center text-slate-500">Loading...</td></tr>
                  ) : campaigns.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                        <p>No campaigns yet</p>
                        <Button 
                          size="sm" 
                          className="mt-3"
                          onClick={() => { resetForm(); setShowCreateCampaign(true); }}
                        >
                          Create Your First Campaign
                        </Button>
                      </td>
                    </tr>
                  ) : campaigns.map(campaign => (
                    <tr key={campaign.id} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <p className="font-medium">{campaign.name}</p>
                        <p className="text-xs text-slate-500">
                          Created {new Date(campaign.created_at).toLocaleDateString()}
                        </p>
                      </td>
                      <td className="p-3">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                          {campaign.template_name}
                        </code>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(campaign.status)}>{campaign.status}</Badge>
                          {campaign.is_running && (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-3 text-sm">
                        {campaign.stats?.total_targeted > 0 ? (
                          <div>
                            <span className="text-green-600">{campaign.stats?.sent || 0}</span>
                            <span className="text-slate-400"> / {campaign.stats?.total_targeted || 0}</span>
                            {campaign.stats?.failed > 0 && (
                              <span className="text-red-500 ml-1">({campaign.stats.failed} failed)</span>
                            )}
                          </div>
                        ) : '-'}
                      </td>
                      <td className="p-3 text-xs text-slate-500">
                        {campaign.min_delay}-{campaign.max_delay}s
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="ghost"><MoreVertical className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {campaign.status === 'draft' && (
                              <>
                                <DropdownMenuItem onClick={() => handleStartCampaign(campaign.id)}>
                                  <Play className="w-4 h-4 mr-2" /> Start Campaign
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditModal(campaign)}>
                                  <Settings className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteCampaign(campaign.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </>
                            )}
                            {campaign.status === 'sending' && (
                              <>
                                <DropdownMenuItem onClick={() => openProgressModal(campaign)}>
                                  <Eye className="w-4 h-4 mr-2" /> View Progress
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handlePauseCampaign(campaign.id)}>
                                  <Pause className="w-4 h-4 mr-2" /> Pause
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStopCampaign(campaign.id)} className="text-red-600">
                                  <XCircle className="w-4 h-4 mr-2" /> Stop
                                </DropdownMenuItem>
                              </>
                            )}
                            {campaign.status === 'paused' && (
                              <>
                                <DropdownMenuItem onClick={() => openProgressModal(campaign)}>
                                  <Eye className="w-4 h-4 mr-2" /> View Progress
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleResumeCampaign(campaign.id)}>
                                  <Play className="w-4 h-4 mr-2" /> Resume
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStopCampaign(campaign.id)} className="text-red-600">
                                  <XCircle className="w-4 h-4 mr-2" /> Stop
                                </DropdownMenuItem>
                              </>
                            )}
                            {['completed', 'stopped', 'failed'].includes(campaign.status) && (
                              <>
                                <DropdownMenuItem onClick={() => openProgressModal(campaign)}>
                                  <Eye className="w-4 h-4 mr-2" /> View Results
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleRestartCampaign(campaign.id)}>
                                  <RefreshCw className="w-4 h-4 mr-2" /> Restart
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteCampaign(campaign.id)} className="text-red-600">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hr">
          <Card className="p-12 text-center">
            <MessageSquare className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold mb-2">HR Contacts Retargeting</h2>
            <p className="text-slate-500 mb-4">
              Create workflows to retarget HR contacts with WhatsApp campaigns.
              This feature is under development.
            </p>
            <Badge variant="outline">Coming Soon</Badge>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Campaign Modal */}
      <Dialog open={showCreateCampaign} onOpenChange={(open) => { if (!open) resetForm(); setShowCreateCampaign(open); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingCampaign ? 'Edit Campaign' : 'Create Retargeting Campaign'}</DialogTitle>
            <DialogDescription>
              Configure WhatsApp retargeting for candidates who haven't registered yet.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-sm font-medium">Campaign Name *</label>
              <Input 
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                placeholder="CV Upload Follow-up"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Interakt Template Name *</label>
              <Input 
                value={campaignForm.template_name}
                onChange={(e) => setCampaignForm({...campaignForm, template_name: e.target.value})}
                placeholder="cv_upload_candidate_ack1"
              />
              <p className="text-xs text-slate-500 mt-1">Must be approved in Interakt</p>
            </div>
            
            <div>
              <label className="text-sm font-medium">Link Base URL</label>
              <Input 
                value={campaignForm.link_base_url}
                onChange={(e) => setCampaignForm({...campaignForm, link_base_url: e.target.value})}
                placeholder="https://daruwallas.com"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Min Delay (seconds)</label>
              <Input 
                type="number"
                min={1}
                max={60}
                value={campaignForm.min_delay}
                onChange={(e) => setCampaignForm({...campaignForm, min_delay: parseInt(e.target.value) || 2})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Max Delay (seconds)</label>
              <Input 
                type="number"
                min={1}
                max={120}
                value={campaignForm.max_delay}
                onChange={(e) => setCampaignForm({...campaignForm, max_delay: parseInt(e.target.value) || 20})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Start Time</label>
              <Input 
                type="time"
                value={campaignForm.start_time}
                onChange={(e) => setCampaignForm({...campaignForm, start_time: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">End Time</label>
              <Input 
                type="time"
                value={campaignForm.end_time}
                onChange={(e) => setCampaignForm({...campaignForm, end_time: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Daily Limit</label>
              <Input 
                type="number"
                min={1}
                max={1000}
                value={campaignForm.daily_limit}
                onChange={(e) => setCampaignForm({...campaignForm, daily_limit: parseInt(e.target.value) || 100})}
              />
            </div>
            
            <div className="col-span-2">
              <label className="text-sm font-medium mb-2 block">Active Days</label>
              <div className="flex flex-wrap gap-2">
                {allDays.map(day => (
                  <Button
                    key={day}
                    variant={campaignForm.active_days.includes(day) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleDay(day)}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1, 3)}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Skip Time Window Toggle */}
            <div className="col-span-2 flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
              <div>
                <label className="text-sm font-medium text-amber-800">Skip Time Window</label>
                <p className="text-xs text-amber-600 mt-0.5">
                  Send messages anytime, ignoring the time window and active days
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCampaignForm({...campaignForm, skip_time_window: !campaignForm.skip_time_window})}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  campaignForm.skip_time_window ? 'bg-amber-500' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    campaignForm.skip_time_window ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            <div className="col-span-2 bg-slate-50 p-3 rounded-lg">
              <h4 className="text-sm font-medium mb-2">Message Preview</h4>
              <div className="text-sm text-slate-600 space-y-2">
                <p>Thank you for applying with Daruwallas! Your resume has been uploaded.</p>
                <p>Top recruiters need an updated profile. Please verify your profile details and make changes if needed: <span className="text-blue-600">{'{candidate_name}'}</span></p>
                <p>You can login using the link button below.</p>
                <p className="text-xs text-slate-400 mt-2">
                  Link: {campaignForm.link_base_url}/{'{portal_slug}'}
                </p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowCreateCampaign(false); }}>
              Cancel
            </Button>
            <Button 
              onClick={editingCampaign ? handleUpdateCampaign : handleCreateCampaign}
              disabled={!campaignForm.name || !campaignForm.template_name}
            >
              {editingCampaign ? 'Update Campaign' : 'Create Campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Progress Modal */}
      <Dialog open={showProgress} onOpenChange={setShowProgress}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{progressCampaign?.name}</span>
              <div className="flex items-center gap-2">
                {progressCampaign?.status === 'sending' && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => handlePauseCampaign(progressCampaign.id)} className="text-yellow-600">
                      <Pause className="w-4 h-4 mr-1" /> Pause
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleStopCampaign(progressCampaign.id)} className="text-red-600">
                      <XCircle className="w-4 h-4 mr-1" /> Stop
                    </Button>
                  </>
                )}
                {progressCampaign?.status === 'paused' && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => handleResumeCampaign(progressCampaign.id)} className="text-green-600">
                      <Play className="w-4 h-4 mr-1" /> Resume
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleStopCampaign(progressCampaign.id)} className="text-red-600">
                      <XCircle className="w-4 h-4 mr-1" /> Stop
                    </Button>
                  </>
                )}
                {['completed', 'stopped', 'failed'].includes(progressCampaign?.status) && (
                  <Button variant="outline" size="sm" onClick={() => handleRestartCampaign(progressCampaign.id)} className="text-blue-600">
                    <RefreshCw className="w-4 h-4 mr-1" /> Restart
                  </Button>
                )}
              </div>
            </DialogTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={getStatusColor(progressCampaign?.status)}>
                {progressCampaign?.status?.toUpperCase()}
              </Badge>
              {progress?.is_running && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span> Live
                </span>
              )}
            </div>
          </DialogHeader>
          
          {progress && (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-slate-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold">{progress.progress?.total || 0}</p>
                  <p className="text-xs text-slate-500">Total</p>
                </div>
                <div className="bg-green-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-green-600">{progress.progress?.sent || 0}</p>
                  <p className="text-xs text-slate-500">Sent</p>
                </div>
                <div className="bg-red-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-red-600">{progress.progress?.failed || 0}</p>
                  <p className="text-xs text-slate-500">Failed</p>
                </div>
                <div className="bg-yellow-50 p-3 rounded text-center">
                  <p className="text-2xl font-bold text-yellow-600">{progress.progress?.pending || 0}</p>
                  <p className="text-xs text-slate-500">Pending</p>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Progress</span>
                  <span>{progress.progress?.percent || 0}%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-3">
                  <div 
                    className="bg-green-500 h-3 rounded-full transition-all"
                    style={{ width: `${progress.progress?.percent || 0}%` }}
                  />
                </div>
              </div>
              
              {/* Recent Activity */}
              {progress.recent_activity?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Recent Activity</h4>
                  <div className="max-h-60 overflow-y-auto space-y-1 bg-slate-50 rounded-lg p-2">
                    {progress.recent_activity.map((item, i) => (
                      <div key={i} className="flex justify-between items-center text-xs py-2 px-2 bg-white rounded">
                        <div>
                          <span className="font-medium">{item.candidate_name}</span>
                          <span className="text-slate-400 ml-2">{item.phone}</span>
                        </div>
                        <Badge className={
                          item.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }>
                          {item.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {progress.is_running && (
                <p className="text-sm text-yellow-600 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" /> Campaign is running... (auto-refreshing)
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Eligible Candidates List Modal */}
      <Dialog open={showEligibleList} onOpenChange={setShowEligibleList}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Eligible Candidates ({eligibleCandidates.length})</DialogTitle>
            <DialogDescription>
              Candidates uploaded via ATS who haven't registered on the portal yet
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh]">
            <table className="w-full">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-3 text-left text-xs font-medium text-slate-600">NAME</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-600">PHONE</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-600">EMAIL</th>
                  <th className="p-3 text-left text-xs font-medium text-slate-600">PORTAL SLUG</th>
                </tr>
              </thead>
              <tbody>
                {eligibleCandidates.map((candidate, idx) => (
                  <tr key={candidate.id || idx} className="border-b hover:bg-slate-50">
                    <td className="p-3 text-sm font-medium">{candidate.name || 'N/A'}</td>
                    <td className="p-3 text-sm">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3 text-slate-400" />
                        {candidate.phone || 'N/A'}
                      </span>
                    </td>
                    <td className="p-3 text-sm text-slate-600">{candidate.email || 'N/A'}</td>
                    <td className="p-3 text-xs">
                      {candidate.portal_slug ? (
                        <code className="bg-slate-100 px-2 py-1 rounded">{candidate.portal_slug}</code>
                      ) : (
                        <span className="text-slate-400">Not set</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Phone Cleanup Modal */}
      <Dialog open={showPhoneCleanup} onOpenChange={setShowPhoneCleanup}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Phone Number Cleanup Preview</DialogTitle>
            <DialogDescription>
              Review the changes before applying. Only valid 10-digit numbers will be updated.
            </DialogDescription>
          </DialogHeader>
          
          {phoneCleanupPreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold">{phoneCleanupPreview.total_candidates}</p>
                  <p className="text-xs text-slate-500">Total Candidates</p>
                </div>
                <div className="bg-green-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{phoneCleanupPreview.to_update}</p>
                  <p className="text-xs text-slate-500">Will Be Cleaned</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{phoneCleanupPreview.skipped_invalid}</p>
                  <p className="text-xs text-slate-500">Skipped (Invalid)</p>
                </div>
              </div>

              {/* Preview Table */}
              {phoneCleanupPreview.preview?.length > 0 && (
                <div className="overflow-y-auto max-h-[40vh] border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Name</th>
                        <th className="p-2 text-left">Original</th>
                        <th className="p-2 text-left">→</th>
                        <th className="p-2 text-left">Cleaned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {phoneCleanupPreview.preview.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="p-2">{item.name?.substring(0, 25)}</td>
                          <td className="p-2 font-mono text-red-600">{item.original}</td>
                          <td className="p-2">→</td>
                          <td className="p-2 font-mono text-green-600">{item.cleaned}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Skipped entries warning */}
              {phoneCleanupPreview.skipped_details?.length > 0 && (
                <div className="bg-amber-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-2">
                    ⚠️ Skipped Invalid Numbers ({phoneCleanupPreview.skipped_invalid}):
                  </p>
                  <div className="text-xs text-amber-700 space-y-1">
                    {phoneCleanupPreview.skipped_details.slice(0, 5).map((item, idx) => (
                      <p key={idx}>• {item.name}: "{item.phone}" - {item.reason}</p>
                    ))}
                  </div>
                </div>
              )}

              {phoneCleanupPreview.to_update === 0 && (
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  <p className="text-green-700 font-medium">All phone numbers are already clean!</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPhoneCleanup(false)}>
              Cancel
            </Button>
            {phoneCleanupPreview?.to_update > 0 && (
              <Button 
                onClick={handlePhoneCleanupApply} 
                disabled={phoneCleanupLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                {phoneCleanupLoading ? 'Applying...' : `Apply Cleanup (${phoneCleanupPreview.to_update} numbers)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RetargetingPage;
